import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramEquivalentRoundDuct } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.equivalentRoundDuct.v1';

type Fields = { a: string; b: string; de: string };
const EMPTY: Fields = { a: '', b: '', de: '' };

/** ASHRAE equivalent round duct (equal friction & flow): Dₑ = 1.30·(a·b)^0.625 / (a+b)^0.25 */
function equivalentRound(a: number, b: number): number {
  return (1.3 * Math.pow(a * b, 0.625)) / Math.pow(a + b, 0.25);
}
function solveSideFromEquivalent(de: number, a: number): number | null {
  let lo = 0.1;
  let hi = 500;
  for (let i = 0; i < 100; i++) {
    const mid = (lo + hi) / 2;
    const val = equivalentRound(a, mid);
    if (Math.abs(val - de) < 1e-4) return mid;
    if (val < de) lo = mid;
    else hi = mid;
  }
  const result = (lo + hi) / 2;
  return result > 0.2 && result < 499 ? result : null;
}

const AirflowEquivalentRoundDuctScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const aN = toImp(fields.a, 'diameter');
    const bN = toImp(fields.b, 'diameter');
    const deN = toImp(fields.de, 'diameter');
    const aValid = aN > 0;
    const bValid = bN > 0;
    const deValid = deN > 0;
    const filled = [aValid, bValid, deValid].filter(Boolean).length;

    if (filled < 2) {
      setError('Enter any 2 of: Side A, Side B, Equivalent Diameter.');
      return;
    }

    if (aValid && bValid) {
      setSolvedFor('de');
      set({ de: fromImp(equivalentRound(aN, bN), 'diameter', 2) });
    } else if (aValid && deValid) {
      const bNew = solveSideFromEquivalent(deN, aN);
      if (bNew == null) {
        setError('Could not solve — check inputs (Dₑ may be inconsistent with Side A).');
        return;
      }
      setSolvedFor('b');
      set({ b: fromImp(bNew, 'diameter', 2) });
    } else if (bValid && deValid) {
      const aNew = solveSideFromEquivalent(deN, bN);
      if (aNew == null) {
        setError('Could not solve — check inputs (Dₑ may be inconsistent with Side B).');
        return;
      }
      setSolvedFor('a');
      set({ a: fromImp(aNew, 'diameter', 2) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.a && { label: 'Side A', value: fields.a, unit: unit('diameter'), emphasis: solvedFor === 'a' },
    fields.b && { label: 'Side B', value: fields.b, unit: unit('diameter'), emphasis: solvedFor === 'b' },
    fields.de && { label: 'Equivalent Diameter Dₑ', value: fields.de, unit: unit('diameter'), emphasis: solvedFor === 'de' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Equivalent Round Duct"
      subtitle="Convert rectangular duct to equivalent round diameter (equal friction & flow)"
      formula={
        <>
          <FormulaLine>Dₑ = 1.30 × (a·b)^0.625 / (a+b)^0.25</FormulaLine>
          <FormulaNote>
            a, b = rectangular sides ({unit('diameter')}); Dₑ = equivalent round diameter ({unit('diameter')}). Fill any 2 fields to solve
            the third.
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      diagram={<DiagramEquivalentRoundDuct />}
      outputSlots={{
        duct_width_in: fields.a ? toImp(fields.a, 'diameter') : undefined,
        duct_height_in: fields.b ? toImp(fields.b, 'diameter') : undefined,
        duct_diameter_in: fields.de ? toImp(fields.de, 'diameter') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.duct_width_in != null && !fields.a) {
          patch.a = fromImp(values.duct_width_in, 'diameter', 2);
        }
        if (values.duct_height_in != null && !fields.b) {
          patch.b = fromImp(values.duct_height_in, 'diameter', 2);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Side A (${unit('diameter')})`} value={fields.a} onChangeText={(v) => set({ a: v })} rightText={unit('diameter')} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Side B (${unit('diameter')})`} value={fields.b} onChangeText={(v) => set({ b: v })} rightText={unit('diameter')} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Equivalent Diameter Dₑ (${unit('diameter')})`} value={fields.de} onChangeText={(v) => set({ de: v })} rightText={unit('diameter')} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
};

export default AirflowEquivalentRoundDuctScreen;
